import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Printer } from 'lucide-react';
import {
  AttendanceApi,
  StaffPerson,
  TimetableApi,
  TimetableDay,
  TimetableSectionGrid,
  TimetableSlot,
  TimetableTeacherGrid,
  TimetableTeacherSlot,
} from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';
import { useAuthStore } from '@/stores/auth.store';
import { isAdminRole } from '@/lib/access';
import { cn } from '@/lib/cn';
import { TimetableCalendarEditor } from './TimetableCalendarEditor';

const DAY_LABEL: Record<TimetableDay, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

/** Union of every distinct (startTime,endTime) across the week, sorted — the row axis, since day/time data has no period-number concept. */
function timeRows<T extends { startTime: string; endTime: string }>(
  days: readonly string[],
  slots: Record<string, T[]>,
): { startTime: string; endTime: string }[] {
  const rows = new Map<string, { startTime: string; endTime: string }>();
  for (const day of days) {
    for (const s of slots[day] ?? []) {
      const key = `${s.startTime}-${s.endTime}`;
      if (!rows.has(key)) rows.set(key, { startTime: s.startTime, endTime: s.endTime });
    }
  }
  return [...rows.values()].sort((a, b) => a.startTime.localeCompare(b.startTime));
}

function WeeklyGridTable<T extends TimetableSlot>({
  days,
  slots,
  renderCellMeta,
  onPrint,
}: {
  days: readonly TimetableDay[];
  slots: Record<string, T[]>;
  renderCellMeta?: (slot: T) => string | null;
  onPrint?: () => void;
}) {
  const rows = useMemo(() => timeRows(days, slots), [days, slots]);
  // A cell can hold MORE THAN ONE slot — real data has genuine overlaps
  // (e.g. two subjects at the same day+time in the same section, an
  // elective split) — never collapse to just the last one.
  const cellMap = useMemo(() => {
    const m = new Map<string, T[]>();
    for (const day of days) {
      for (const s of slots[day] ?? []) {
        const key = `${day}:${s.startTime}-${s.endTime}`;
        (m.get(key) ?? m.set(key, []).get(key)!).push(s);
      }
    }
    return m;
  }, [days, slots]);
  const todayName = Object.keys(DAY_LABEL)[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  if (rows.length === 0) {
    return <div className="card p-8 text-center text-slate-500">No timetable recorded for this selection.</div>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      {onPrint && (
        <div className="flex justify-end border-b border-slate-100 px-3 py-2">
          <button className="btn-secondary text-xs" onClick={onPrint}>
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print / PDF
          </button>
        </div>
      )}
      <table className="min-w-full border-separate border-spacing-0">
        <thead>
          <tr className="bg-slate-50">
            <th className="sticky left-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              Time
            </th>
            {days.map((d) => (
              <th
                key={d}
                className={cn(
                  'border-b border-l border-slate-200 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider',
                  d === todayName ? 'bg-brand-100 text-brand-700' : 'text-slate-500',
                )}
              >
                {DAY_LABEL[d]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.startTime}-${row.endTime}`}>
              <td className="sticky left-0 z-10 border-b border-slate-100 bg-white px-3 py-2 text-xs font-medium text-slate-600 whitespace-nowrap">
                {row.startTime}–{row.endTime}
              </td>
              {days.map((d) => {
                const cellSlots = cellMap.get(`${d}:${row.startTime}-${row.endTime}`);
                return (
                  <td key={d} className="border-b border-l border-slate-100 px-2 py-2 text-center align-top">
                    {!cellSlots?.length ? (
                      <span className="text-slate-200">·</span>
                    ) : (
                      <div className="space-y-1.5">
                        {cellSlots.map((slot) =>
                          slot.type === 'Break' ? (
                            <Badge key={slot.id} tone="slate">Break</Badge>
                          ) : (
                            <div key={slot.id}>
                              <div className="text-sm font-medium text-slate-800">
                                {slot.subject || '—'}
                              </div>
                              <div
                                className={cn(
                                  'text-[11px]',
                                  slot.teacher === 'Unassigned' ? 'text-amber-600' : 'text-slate-400',
                                )}
                              >
                                {slot.teacher}
                              </div>
                              {renderCellMeta && (
                                <div className="text-[10px] text-slate-400">{renderCellMeta(slot)}</div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───── Section timetable ("Timetable" screen) ─────────────────────────────────
export function TimetablePage() {
  const term = useTerminology();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = isAdminRole(role);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [editing, setEditing] = useState(false);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  useEffect(() => {
    if (sections.length && !sections.find((s) => s.id === sectionId)) {
      setSectionId(sections[0].id);
    } else if (!sections.length) setSectionId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  useEffect(() => setEditing(false), [sectionId]);

  const { data: grid, isLoading } = useQuery<TimetableSectionGrid>({
    queryKey: ['timetable', 'section', sectionId],
    queryFn: () => TimetableApi.sectionGrid(sectionId),
    enabled: !!sectionId && !editing,
  });

  return (
    <>
      <PageHeader
        title="Timetable"
        description={`Pick a ${term.level.toLowerCase()} and ${term.group.toLowerCase()} to see its weekly timetable.`}
        actions={
          canEdit && sectionId && !editing ? (
            <button className="btn-secondary" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Edit timetable
            </button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          className="!w-44"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">Select {term.level.toLowerCase()}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select
          className="!w-36"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId || sections.length === 0}
        >
          {sections.length === 0 ? (
            <option value="">— No {term.groupPlural.toLowerCase()} —</option>
          ) : (
            sections.map((s) => (
              <option key={s.id} value={s.id}>{term.group} {s.name}</option>
            ))
          )}
        </Select>
      </div>

      {!sectionId ? (
        <div className="card p-8 text-center text-slate-500">
          Pick a {term.level.toLowerCase()} and {term.group.toLowerCase()} to view its timetable.
        </div>
      ) : editing ? (
        <TimetableCalendarEditor sectionId={sectionId} onDone={() => setEditing(false)} />
      ) : isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !grid || !grid.hasData ? (
        <div className="card flex flex-col items-center gap-3 p-8 text-center">
          <div className="text-slate-500">
            No timetable recorded for {grid?.section.className} {grid?.section.name ?? 'this section'} yet.
          </div>
          {canEdit && (
            <button className="btn-primary" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Build timetable
            </button>
          )}
        </div>
      ) : (
        <WeeklyGridTable
          days={grid.days}
          slots={grid.slots}
          onPrint={() => printGrid(`${grid.section.className} ${grid.section.name}`, grid.days, grid.slots)}
        />
      )}
    </>
  );
}

// ───── Teacher timetable ("Teacher Timetable" screen) ─────────────────────────
export function TeacherTimetablePage() {
  const [teacherId, setTeacherId] = useState('');

  const { data: teachers = [] } = useQuery<StaffPerson[]>({
    queryKey: ['staff-people', 'teacher'],
    queryFn: () => AttendanceApi.staffPeople('teacher'),
  });

  const { data: grid, isLoading } = useQuery<TimetableTeacherGrid>({
    queryKey: ['timetable', 'teacher', teacherId],
    queryFn: () => TimetableApi.teacherGrid(teacherId),
    enabled: !!teacherId,
  });

  return (
    <>
      <PageHeader title="Teacher Timetable" description="Pick a teacher to see their weekly schedule across every section they teach." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
          <option value="">— Select teacher —</option>
          {teachers.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </Select>
      </div>

      {!teacherId ? (
        <div className="card p-8 text-center text-slate-500">Pick a teacher to view their timetable.</div>
      ) : isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !grid || !grid.hasData ? (
        <div className="card p-8 text-center text-slate-500">
          {grid?.teacher.name ?? 'This teacher'} has no scheduled periods yet.
        </div>
      ) : (
        <WeeklyGridTable<TimetableTeacherSlot>
          days={grid.days}
          slots={grid.slots}
          renderCellMeta={(slot) => slot.section}
          onPrint={() => printGrid(grid.teacher.name, grid.days, grid.slots, true)}
        />
      )}
    </>
  );
}

// ───── Print ────────────────────────────────────────────────────────────────
function printGrid(
  title: string,
  days: readonly TimetableDay[],
  slots: Record<string, TimetableSlot[]>,
  showSection = false,
) {
  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
  const rows = timeRows(days, slots);
  // A cell can hold more than one slot (real overlaps in the data) — never
  // collapse to just the last one.
  const cellMap = new Map<string, TimetableSlot[]>();
  for (const day of days) {
    for (const s of slots[day] ?? []) {
      const key = `${day}:${s.startTime}-${s.endTime}`;
      (cellMap.get(key) ?? cellMap.set(key, []).get(key)!).push(s);
    }
  }

  const head = `<th class="pcol">Time</th>` + days.map((d) => `<th>${DAY_LABEL[d]}</th>`).join('');
  const bodyRows = rows
    .map((row) => {
      const cells = days
        .map((d) => {
          const cellSlots = cellMap.get(`${d}:${row.startTime}-${row.endTime}`);
          if (!cellSlots?.length) return `<td class="empty">·</td>`;
          const inner = cellSlots
            .map((slot) => {
              if (slot.type === 'Break') return `<div class="brk">Break</div>`;
              const sub = esc(slot.subject || '');
              const teacher = !showSection && slot.teacher ? `<div class="tch">${esc(slot.teacher)}</div>` : '';
              const section = showSection && (slot as TimetableTeacherSlot).section
                ? `<div class="tch">${esc((slot as TimetableTeacherSlot).section)}</div>`
                : '';
              return `<div class="sub">${sub}</div>${teacher}${section}`;
            })
            .join('<hr class="sep"/>');
          return `<td>${inner}</td>`;
        })
        .join('');
      return `<tr><td class="pcol"><div class="pt">${esc(row.startTime)}–${esc(row.endTime)}</div></td>${cells}</tr>`;
    })
    .join('');

  const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Timetable — ${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #1e293b; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: center; vertical-align: middle; }
  th { background: #f1f5f9; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #475569; }
  .pcol { text-align: left; white-space: nowrap; background: #f8fafc; }
  .pt { font-size: 11px; font-weight: 600; color: #475569; }
  .sub { font-weight: 600; font-size: 13px; }
  .tch { font-size: 10px; color: #64748b; }
  .brk { font-size: 11px; text-transform: uppercase; color: #94a3b8; }
  .sep { border: none; border-top: 1px dashed #e2e8f0; margin: 4px 0; }
  td.empty { color: #cbd5e1; }
  .ft { margin-top: 14px; font-size: 10px; color: #94a3b8; }
  @media print { body { margin: 0; } @page { margin: 14mm; } }
</style></head><body>
  <h1>${esc(title)} — Weekly Timetable</h1>
  <table><thead><tr>${head}</tr></thead><tbody>${bodyRows}</tbody></table>
  <div class="ft">Generated from EduPro.</div>
  <script>window.onload = function(){ window.print(); };</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=900,height=650');
  if (!w) {
    window.print();
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
